import { useCallback } from "react";
import { readCreditOffer } from "@/utils/creditOffer";
import {
  trackCreditPurchaseIntent,
  trackEvent,
  trackCreditCheckoutStart,
  trackCreditModalAction,
} from "@/utils/analytics";
import { logger } from "@/utils/logger";
import { FormPersistenceService } from "@/services/formPersistenceService";
import { useCreditsContext } from "@/context/providers/CreditsProvider";
import { useCreatePageStore } from "@/stores/createPageStore";
import SavePoint from "@/utils/savePoint";
import type { CreditModalContext } from "@/components/create/creditModalContext";
import { upgradeAnonymousAccount } from "@/lib/auth/anonymousSession";
import { clearCreditPurchaseAfterAuth } from "@/utils/creditAuthIntentPersistence";
import {
  CREDIT_AUTH_HANDOFF_ERROR,
  prepareCreditAccountUpgradeHandoff,
} from "@/utils/creditAuthHandoff";
import {
  buildCreditCheckoutReturnUrl,
  CREDIT_CHECKOUT_HANDOFF_ERROR,
  createCreditCheckoutRestoreId,
} from "@/utils/creditCheckoutReturnUrl";
import {
  getGeneratedDesignSpec,
  getGeneratedFrontDesignSpec,
} from "@/lib/designer/savedDesign";
import type { DesignSpec } from "@/types/designSpec";
import { prepareSavePointPayload } from "../utils/formStateHelpers";
import { ActionHookContext } from "./types";

export const CREDIT_ACCOUNT_REFRESH_DEADLINE_MS = 10_000;

async function refreshAccountWithinDeadline(
  refreshSession: () => Promise<void>
): Promise<boolean> {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  try {
    return await Promise.race([
      refreshSession().then(
        () => true,
        // The checkout API revalidates the server session. A rejected UI
        // refresh is therefore recoverable; only a promise that never settles
        // may keep the non-dismissible account gate locked forever.
        () => true
      ),
      new Promise<boolean>(resolve => {
        timeoutId = setTimeout(
          () => resolve(false),
          CREDIT_ACCOUNT_REFRESH_DEADLINE_MS
        );
      }),
    ]);
  } finally {
    if (timeoutId !== null) clearTimeout(timeoutId);
  }
}

export function useCreditActions(
  context: ActionHookContext,
  submitTicket: (
    designSpec?: DesignSpec,
    options?: { quoteScope?: "front_only" }
  ) => Promise<unknown>
) {
  const { store, stateRef, refreshSession, identityEpochRef, session } =
    context;

  // Get credit-related values from context
  const {
    refresh: invalidateCredits,
    balance, // Credit balance from useCreditsContext() - single source of truth
  } = useCreditsContext();

  const showCreditModal = useCallback(
    (context?: NonNullable<CreditModalContext>) => {
      store.setShowCreditModal(context);
    },
    [store]
  );

  const hideCreditModal = useCallback(() => {
    const currentState = useCreatePageStore.getState();
    const hasGeneratedImage = !!(
      currentState.generation.images.front.url ||
      currentState.generation.images.front.path
    );

    // Track credit modal action (balance from useCredits() - single source of truth)
    trackCreditModalAction({
      action: hasGeneratedImage ? "submit_front_only" : "dismissed",
      credits_shown: balance,
      step: currentState.form.currentStep,
      has_generated_image: hasGeneratedImage,
    });

    // Clear loading state when modal is closed
    store.setCreditPurchaseLoading(false);
    store.hideCreditModal();
    // Mark as dismissed so it doesn't reopen automatically
    store.setCreditModalDismissed(true);

    // The alternative offered by the credit wall is a quote for the image the
    // customer can see. Use its saved render contract, not an edited draft that
    // could not be applied because the customer ran out of credits.
    if (hasGeneratedImage) {
      const images = currentState.generation.images;
      const frontOnly = !images.back.url?.trim();
      const savedSpec =
        !images.front.path?.trim() || (!frontOnly && !images.back.path?.trim())
          ? null
          : frontOnly
            ? getGeneratedFrontDesignSpec(images.front)
            : getGeneratedDesignSpec(images);
      if (savedSpec) {
        void submitTicket(
          savedSpec,
          frontOnly ? { quoteScope: "front_only" } : undefined
        );
      } else {
        // A legacy preview without its render contract cannot establish which
        // details were visible in the image. The draft may contain an
        // unrendered Back even when the change planner reports no edits.
        store.setError(
          "This saved preview could not be verified for a quote. Generate a new preview, then try again."
        );
      }
    }
  }, [store, submitTicket, balance]);

  const dismissCreditModal = useCallback(() => {
    const currentState = stateRef.current;

    // Track credit modal dismissal (balance from useCredits() - single source of truth)
    trackCreditModalAction({
      action: "dismissed",
      credits_shown: balance,
      step: currentState.form.currentStep,
      has_generated_image: !!(
        currentState.generation.images.front.url ||
        currentState.generation.images.front.path
      ),
    });

    // Clear loading state when modal is dismissed
    store.setCreditPurchaseLoading(false);
    // Just close the modal without submitting - used for X button
    store.hideCreditModal();
    store.setCreditModalDismissed(true);
  }, [store, stateRef, balance]);

  const purchaseCredits = useCallback(
    async (credits: number = 5, price: number = 6.99, priceId?: string) => {
      const currentState = stateRef.current;
      let checkoutRestoreId: string | null = null;

      try {
        // Track credit purchase intent
        trackCreditPurchaseIntent({
          step_index: currentState.form.currentStep,
          source: "modal",
        });

        // Track purchase credits click from modal
        trackEvent("purchase_credits_click", {
          source: "modal",
          step_index: currentState.form.currentStep,
          page_section: "create_page",
          credits,
          price,
        });

        // Set loading state - keep modal open with spinner
        store.setCreditPurchaseLoading(true);

        // Save comprehensive state before redirect (using same helper as sign-in flow)
        // This ensures consistent serialization logic for both auth and credit purchase flows
        logger.debug("purchaseCredits: Preparing to save form state", {
          component: "CreatePage",
          action: "purchaseCredits",
          currentStep: currentState.form.currentStep,
          hasPrompt: !!currentState.form.data.prompt?.trim(),
          promptLength: currentState.form.data.prompt?.trim().length || 0,
          hasImages: !!currentState.generation.images.front.url,
          hasImagePath: !!currentState.generation.images.front.path,
          hasBackImages: !!currentState.generation.images.back.url,
          hasVariations:
            currentState.generation.images.variations.front.length > 0 ||
            currentState.generation.images.variations.back.length > 0,
        });

        const formDataToSave = prepareSavePointPayload(currentState);
        const ownerUserId = session?.user?.id?.trim();
        checkoutRestoreId = createCreditCheckoutRestoreId();
        if (!ownerUserId || !checkoutRestoreId) {
          throw new Error(CREDIT_CHECKOUT_HANDOFF_ERROR);
        }

        // Check if we have a save point from when the credit modal was shown
        // If so, use that step and modification state instead of the current (reverted) step
        const existingSavePoint = SavePoint.getLatestSavePoint();
        if (
          existingSavePoint?.source === "credit_modal_shown" &&
          existingSavePoint.data.currentStep
        ) {
          // Use the original step from when the modal was shown
          formDataToSave.currentStep = existingSavePoint.data.currentStep;

          // Preserve modification state from when they ran out of credits
          // This ensures they return to the exact context where they were trying to modify
          if (existingSavePoint.data.modifyPrompt) {
            formDataToSave.modifyPrompt = existingSavePoint.data.modifyPrompt;
          }
          if (existingSavePoint.data.showModifyInput !== undefined) {
            formDataToSave.showModifyInput =
              existingSavePoint.data.showModifyInput;
          }
          if (existingSavePoint.data.currentView !== undefined) {
            formDataToSave.currentView = existingSavePoint.data.currentView;
          }

          logger.info(
            "Using original step and modification context from credit modal save point",
            {
              component: "useCreditActions",
              action: "purchaseCredits",
              originalStep: existingSavePoint.data.currentStep,
              currentStep: currentState.form.currentStep,
              modifyPrompt: existingSavePoint.data.modifyPrompt,
              showModifyInput: existingSavePoint.data.showModifyInput,
              currentView: existingSavePoint.data.currentView,
            }
          );
        }

        logger.info("Saving state before credit purchase", {
          component: "CreatePage",
          action: "purchaseCredits",
          modifyPrompt: currentState.ui.modification.prompt,
          showModifyInput: currentState.ui.modification.showInput,
          currentView: currentState.ui.view,
          currentStep: currentState.form.currentStep,
          savedStep: formDataToSave.currentStep,
          hasImages: !!currentState.generation.images.front.url,
          hasFrontVariations:
            currentState.generation.images.variations.front.length > 0,
          hasBackVariations:
            currentState.generation.images.variations.back.length > 0,
          selectedFrontVariationIndex:
            currentState.generation.images.selected.frontIndex,
          selectedBackVariationIndex:
            currentState.generation.images.selected.backIndex,
          formDataKeys: Object.keys(formDataToSave.formData || {}),
          hasFormData: !!formDataToSave.formData,
          hasImageUrl: !!formDataToSave.imageUrl,
          hasImagePath: !!formDataToSave.imagePath,
          hasTicketId: !!formDataToSave.ticketId,
        });

        FormPersistenceService.saveState({
          data: formDataToSave,
          source: "credit_purchase_redirect",
          intentId: checkoutRestoreId,
          ownerUserId,
        });

        // Stripe can return in a new tab, where sessionStorage is empty. Do
        // not take payment unless this exact, account-bound restore is already
        // readable from durable storage; SavePoint writes are intentionally
        // best-effort and therefore require this explicit readback.
        const durableSavePoint =
          SavePoint.getDurableSavePoint(checkoutRestoreId);
        if (
          durableSavePoint?.version !== 1 ||
          durableSavePoint.source !== "credit_purchase_redirect" ||
          durableSavePoint.intentId !== checkoutRestoreId ||
          durableSavePoint.ownerUserId !== ownerUserId ||
          !durableSavePoint.data
        ) {
          throw new Error(CREDIT_CHECKOUT_HANDOFF_ERROR);
        }

        logger.debug("purchaseCredits: Form state saved successfully", {
          component: "CreatePage",
          action: "purchaseCredits",
          source: "credit_purchase_redirect",
          timestamp: Date.now(),
        });

        // Track credit checkout start
        trackCreditCheckoutStart({
          step_index: currentState.form.currentStep,
          source: "modal",
        });

        // Get current URL to redirect back after purchase
        const currentUrl = buildCreditCheckoutReturnUrl(window.location.href, {
          restoreForm: true,
          restoreId: checkoutRestoreId,
        });
        const config = {
          type: "credit" as const,
          amount: price,
          currency: readCreditOffer(priceId)?.currency.toUpperCase() ?? "USD",
          price: price,
          returnUrl: currentUrl,
          priceId: priceId,
          credits: credits, // unified checkout expects this
        };

        // Clear loading and hide modal when checkout opens
        store.setCreditPurchaseLoading(false);
        store.hideCreditModal();
        store.setShowCreditCheckout(config);
      } catch (error) {
        if (checkoutRestoreId) {
          SavePoint.clear(checkoutRestoreId);
        }
        // Clear loading state on error
        store.setCreditPurchaseLoading(false);
        logger.error(
          "Error during credit purchase setup",
          {
            component: "useCreditActions",
            action: "purchaseCredits",
            credits,
            price,
          },
          error instanceof Error ? error : new Error(String(error))
        );
        // Re-throw to let caller handle the error
        throw error;
      }
    },
    [store, stateRef, session?.user?.id]
  );

  /**
   * The "one chore" unlock: create the account from the current anonymous
   * session, then start credit checkout, as a single user action.
   *
   * Order is load-bearing. /api/credits/checkout is auth-required and attaches
   * the purchase to the caller's user id, so the account must exist first. And
   * because the upgrade happens IN PLACE — same auth.users row — that id is
   * already the one that owns the design on screen and the agent session, so
   * the Stripe round trip returns to the same dress with nothing to migrate.
   *
   * Never throws: it returns a result the modal renders inline. Throwing would
   * close over the customer's typed email and lose it.
   *
   * VERIFIED CONSTRAINT — `requiresConfirmation`. Until the emailed link is
   * clicked, Supabase leaves this identity anonymous and password sign-in is
   * unavailable. Checkout therefore pauses at confirmation instead of putting
   * paid credits on an account the customer cannot recover.
   */
  const upgradeAndPurchaseCredits = useCallback(
    async (params: {
      email: string;
      password: string;
      credits: number;
      price: number;
      stripePriceId: string;
    }): Promise<{
      ok: boolean;
      error?: string;
      requiresConfirmation?: boolean;
    }> => {
      const identityEpoch = identityEpochRef.current;
      const currentState = stateRef.current;
      const handoff = prepareCreditAccountUpgradeHandoff({
        state: currentState,
        currentHref: window.location.href,
        credits: params.credits,
        offerToken: params.stripePriceId.startsWith("credit_offer_")
          ? params.stripePriceId
          : undefined,
      });
      if (!handoff) {
        return { ok: false, error: CREDIT_AUTH_HANDOFF_ERROR };
      }

      const intentId = handoff.intentId;
      const clearIntentSavePoint = () => {
        SavePoint.clear(intentId);
      };
      const confirmationReturnPath = handoff.returnPath;

      const upgrade = await upgradeAnonymousAccount({
        email: params.email,
        password: params.password,
        agentSessionId: currentState.agent.sessionId,
        redirectTo: confirmationReturnPath,
      });

      if (identityEpochRef.current !== identityEpoch) {
        clearCreditPurchaseAfterAuth(intentId);
        clearIntentSavePoint();
        return {
          ok: false,
          error: "Your session changed. Please choose the credit pack again.",
        };
      }

      if (!upgrade.ok) {
        clearCreditPurchaseAfterAuth(intentId);
        clearIntentSavePoint();
        logger.warn("Anonymous upgrade failed during credit unlock", {
          component: "useCreditActions",
          action: "upgradeAndPurchaseCredits",
          metadata: { error: upgrade.error },
        });
        return {
          ok: false,
          error:
            upgrade.error === "email_taken"
              ? "That email already has an account. Sign in instead and your design will still be here."
              : upgrade.error === "validation_failed"
                ? (upgrade.description ??
                  "Check the email and password and try again.")
                : "We couldn't create your account. Please try again.",
        };
      }

      trackEvent("anonymous_account_upgraded", {
        journey_stage: "create",
        page_section: "create_page",
        credits: params.credits,
      });

      if (upgrade.requiresConfirmation) {
        window.history.replaceState({}, "", confirmationReturnPath);
      }

      // The checkout request must carry the upgraded session's cookies, and
      // React state must agree, or the very next render re-raises this wall.
      // Confirmation is different: show the actionable email step
      // immediately and let auth hydration finish in the background instead
      // of holding a non-dismissible form on auxiliary session/role data.
      if (upgrade.requiresConfirmation) {
        void refreshSession().catch(() => {});
        return { ok: true, requiresConfirmation: true };
      }

      const sessionRefreshSettled =
        await refreshAccountWithinDeadline(refreshSession);
      if (identityEpochRef.current !== identityEpoch) {
        clearCreditPurchaseAfterAuth(intentId);
        clearIntentSavePoint();
        return {
          ok: false,
          error: "Your session changed. Please choose the credit pack again.",
        };
      }
      if (!sessionRefreshSettled) {
        clearCreditPurchaseAfterAuth(intentId);
        clearIntentSavePoint();
        return {
          ok: false,
          error:
            "Your account is ready, but checkout couldn't open. Refresh this page, then choose a pack again.",
        };
      }
      clearCreditPurchaseAfterAuth(intentId);
      clearIntentSavePoint();

      try {
        await purchaseCredits(
          params.credits,
          params.price,
          params.stripePriceId
        );
        return { ok: true, requiresConfirmation: false };
      } catch (error) {
        logger.error(
          "Credit checkout failed after anonymous upgrade",
          {
            component: "useCreditActions",
            action: "upgradeAndPurchaseCredits",
          },
          error instanceof Error ? error : new Error(String(error))
        );
        // The account DID get created — say so, so the customer does not try to
        // sign up a second time with the same address and hit "email taken".
        return {
          ok: false,
          error:
            "Your account is ready, but checkout didn't open. Please pick a pack again.",
        };
      }
    },
    [identityEpochRef, purchaseCredits, refreshSession, stateRef]
  );

  const handleCreditSuccess = useCallback(
    (data: { type: string; credits?: number; transactionId?: string }) => {
      if (data.type === "credit" && data.credits && data.transactionId) {
        store.setShowCreditSuccess({
          credits: data.credits,
          transactionId: data.transactionId,
        });
        // Note: Credits are invalidated when the success modal closes (hideCreditSuccess)
        // or when returning from Stripe redirect (useCreditManagement detects credits_purchased param)
      }
    },
    [store]
  );

  const hideCreditSuccess = useCallback(() => {
    store.hideCreditSuccess();
    // Reset dismissed flag after successful purchase so they can use the app normally
    store.setCreditModalDismissed(false);
    // Fire and forget - credits will update in the background
    invalidateCredits().catch(error => {
      logger.error("Failed to invalidate credits after closing success modal", {
        component: "useCreatePageActions",
        error: error instanceof Error ? error.message : String(error),
      });
    });
  }, [store, invalidateCredits]);

  const hideCreditCheckout = useCallback(() => {
    store.hideCreditCheckout();
  }, [store]);

  return {
    showCreditModal,
    hideCreditModal,
    dismissCreditModal,
    purchaseCredits,
    upgradeAndPurchaseCredits,
    handleCreditSuccess,
    hideCreditSuccess,
    hideCreditCheckout,
  };
}
